import * as React from 'react';
import {CSATSurveyResponses} from '@discordapp/common/shared-constants/CSATSurveyResponses';
import {useStateFromStores} from '@discordapp/flux';
import {Storage} from '@discordapp/storage';

import {trackDevPortalCSATSurveyResponse} from '@developers/actions/AnalyticsActions';
import UserStore from '@developers/stores/UserStore';
import Tooltip from '@developers/uikit/Tooltip';
import {track} from '@developers/utils/TrackingAnalyticsUtils';
import SidebarCta, {
  SidebarCtaActionTypes,
  SidebarCtaActions,
  SidebarCtaAction,
  SidebarCtaContent,
  SidebarCtaHeading,
  SidebarCtaCloseButton,
} from './SidebarCta';

import {AnalyticsEvents, SIDEBAR_SURVEY_CONTENT_PROMPT_VISIBILITY_KEY} from '@developers/Constants';
import {Messages} from '@developers/i18n';

const CTA_ANALYTICS_NAME = 'sidebar_survey_content_cta';
const CTA_VISIBILITY_KEY_TTL_DAYS = 90;

interface SidebarSurveyContentCtaStorageValue {
  expiry: number;
}

export function shouldShowSurveyContentCta() {
  const storageObject = Storage.get(SIDEBAR_SURVEY_CONTENT_PROMPT_VISIBILITY_KEY) as
    | SidebarSurveyContentCtaStorageValue
    | undefined;

  if (storageObject === undefined) {
    return true;
  }

  return storageObject.expiry < new Date().getTime();
}

export default function SurveyContentCta() {
  const [shouldDisplayPrompt, setShouldDisplayPrompt] = React.useState(shouldShowSurveyContentCta());

  const user = useStateFromStores([UserStore], () => UserStore.user);
  React.useEffect(() => {
    if (shouldDisplayPrompt && user != null) {
      track(AnalyticsEvents.CTA_VIEWED, {
        user_id: user.id,
        cta_name: CTA_ANALYTICS_NAME,
      });
    }
  }, [shouldDisplayPrompt, user]);

  if (!shouldDisplayPrompt || user == null) {
    return null;
  }

  const handleCtaClick = (userResponse: Values<typeof CSATSurveyResponses>) => {
    if (user != null) {
      trackDevPortalCSATSurveyResponse({
        user_id: user.id,
        csat_response: userResponse,
      });
    }

    dismissPrompt();
  };

  const dismissPrompt = () => {
    const timestamp = new Date();
    Storage.set(SIDEBAR_SURVEY_CONTENT_PROMPT_VISIBILITY_KEY, {
      expiry: timestamp.setDate(timestamp.getDate() + CTA_VISIBILITY_KEY_TTL_DAYS),
    });
    setShouldDisplayPrompt(false);
  };

  const options = [
    [
      Messages.DEVELOPER_EXPERIENCE_SURVEY_VERY_UNSATISFIED,
      Messages.DEVELOPER_EXPERIENCE_SURVEY_VERY_UNSATISFIED_OPTION,
      CSATSurveyResponses.VERY_UNSATISFIED,
    ],
    [
      Messages.DEVELOPER_EXPERIENCE_SURVEY_UNSATISFIED,
      Messages.DEVELOPER_EXPERIENCE_SURVEY_UNSATISFIED_OPTION,
      CSATSurveyResponses.UNSATISFIED,
    ],
    [
      Messages.DEVELOPER_EXPERIENCE_SURVEY_NEUTRAL,
      Messages.DEVELOPER_EXPERIENCE_SURVEY_NEUTRAL_OPTION,
      CSATSurveyResponses.NEUTRAL,
    ],
    [
      Messages.DEVELOPER_EXPERIENCE_SURVEY_SATISFIED,
      Messages.DEVELOPER_EXPERIENCE_SURVEY_SATISFIED_OPTION,
      CSATSurveyResponses.SATISFIED,
    ],
    [
      Messages.DEVELOPER_EXPERIENCE_SURVEY_VERY_SATISFIED,
      Messages.DEVELOPER_EXPERIENCE_SURVEY_VERY_SATISFIED_OPTION,
      CSATSurveyResponses.VERY_SATISFIED,
    ],
  ] as const;

  return (
    <SidebarCta>
      <SidebarCtaHeading>{Messages.DEVELOPER_EXPERIENCE_SURVEY_SURVEY_HEADING}</SidebarCtaHeading>
      <SidebarCtaCloseButton onClick={dismissPrompt} />
      <SidebarCtaContent>{Messages.DEVELOPER_EXPERIENCE_SURVEY_CSAT_SURVEY_PITCH}</SidebarCtaContent>
      <SidebarCtaActions>
        {options.map(([tooltipMessage, message, response], index) => (
          <Tooltip key={index} text={tooltipMessage}>
            <div>
              <SidebarCtaAction
                onClick={() => {
                  handleCtaClick(response);
                }}
                type={SidebarCtaActionTypes.TERTIARY}>
                {message}
              </SidebarCtaAction>
            </div>
          </Tooltip>
        ))}
      </SidebarCtaActions>
    </SidebarCta>
  );
}
